import { COR_PADRAO_ONIBUS, POSICOES_FINAIS_ONIBUS, poseDoOnibus, type PosicaoFinalOnibus } from '../../dominio/tabletop/onibus'
import { comDirecao, direcaoParaAlvo } from '../../dominio/tabletop/direcao'
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import { perfilVeiculoPorId, type PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import {
  PROTECOES_INICIAIS,
  type FerramentaVidro,
  type IdVidro,
  type ResultadoAcaoVidro,
} from '../../dominio/didatico/manejoVidros'
import type { EquipamentoEstabilizacao } from '../../dominio/didatico/estabilizacao'
import {
  TRANSFORMACAO_TABLETOP_PADRAO,
  adicionarObjeto,
  adicionarAmassadoNoPonto,
  limparAmassadosDoVeiculo,
  ancorasLivresDoVeiculo,
  aplicarDeformacaoNaRegiao,
  atualizarObjeto,
  atualizarTransformacao,
  calcularDeformacaoFrontalPredefinida,
  deformacaoPredefinidaNaPorta,
  criarCenaVazia,
  criarPresetTabletopComplexo,
  definirFichaDaVitima,
  encaixarPosicaoXZ,
  obterPresetPorId,
  desfazer as desfazerHistorico,
  desserializarCenaTabletop,
  desvincularParaOSolo,
  duplicarObjeto,
  executarCorteNoVeiculo,
  executarManejoVidroNoVeiculo,
  filaDeAtendimento,
  historicoVazio,
  moverNaFila as moverNaFilaDominio,
  podeDesfazer as podeDesfazerHistorico,
  podeRefazer as podeRefazerHistorico,
  poseDaPosicaoFinal,
  refazer as refazerHistorico,
  registrar,
  removerObjeto,
  renomearCena,
  restaurarEstruturaDoVeiculo,
  restaurarVidrosDoVeiculo,
  selecionarPecaNoVeiculo,
  serializarCenaTabletop,
  soltarVitimasDoVeiculoRemovido,
  vincularVitimaAoVeiculo,
  type BlocoRiscoTabletop,
  type BlocoVeiculoTabletop,
  type IdPortaTabletop,
  type BlocoVitimaTabletop,
  type CenaTabletop,
  type EstadoCortePecaTabletop,
  type Historico,
  type ObjetoTabletop,
  type PosicaoFinalVeiculo,
  type PosturaVitimaSolo,
  type ResultadoIntervencaoVeiculoTabletop,
  type TipoObjetoTabletop,
  type TransformacaoTabletop,
} from '../../dominio/tabletop'
import type { NivelDanoVeicular, RegiaoDanoVeicular } from '../../dominio/didatico/danoVeicular'
import { definirAcionamento } from '../../dominio/tabletop/atendimento/acionamento'
import { MATERIAIS_PADRAO_DERRAMAMENTO } from '../../dominio/tabletop/atendimento/riscos'
import type { AcionamentoCobom } from '../../dominio/tabletop/tipos'
import type { FerramentaTabletop, PedidoTesteBalanco } from '../../visualizacao3d/tabletop/CenaTabletop'
import { tocarSomCorteMetal, tocarSomEstilhacoVidro } from '../audio/somOperacional'

const CHAVE_TABLETOP_LOCAL = 'csv-lab-tabletop-v2'
const CHAVE_TABLETOP_LEGADA = 'csv-lab-diorama-v1'

function carregarTabletopLocal(): CenaTabletop {
  if (typeof window === 'undefined') return criarCenaVazia()
  try {
    const salvo = window.localStorage.getItem(CHAVE_TABLETOP_LOCAL) ?? window.localStorage.getItem(CHAVE_TABLETOP_LEGADA)
    return salvo ? desserializarCenaTabletop(salvo) : criarCenaVazia()
  } catch {
    return criarCenaVazia()
  }
}

function novoIdTabletop(): string {
  return globalThis.crypto?.randomUUID?.() ?? `tabletop-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const TIPOS_VEICULO_NO_CATALOGO: readonly TipoObjetoTabletop[] = ['carro', 'suv']
const TIPOS_EQUIPAMENTO_NO_CATALOGO: readonly TipoObjetoTabletop[] = ['calco', 'cunha', 'vstrut', 'fitaCatraca', 'bloco']

/**
 * Blocos que dão sentido de imediato ao objeto recém-inserido: um carro já
 * nasce com posição final e proteções padrão (para a estabilização e o
 * manejo de vidros por veículo terem o que avaliar), uma vítima já nasce com
 * triagem amarela no solo (o instrutor reclassifica e vincula ao assento
 * depois), e um equipamento de estabilização já carrega seu próprio tipo.
 */
function blocoPadraoTabletop(tipo: TipoObjetoTabletop): Pick<ObjetoTabletop, 'veiculo' | 'vitima' | 'equipamento' | 'risco' | 'cor'> {
  if (TIPOS_VEICULO_NO_CATALOGO.includes(tipo)) {
    return {
      veiculo: {
        idPerfil: (tipo === 'suv' ? 'sedanMedio' : 'hatchCompacto') as PerfilVeiculo['id'],
        posicaoFinal: 'sobreRodas' as const,
        estadosPecas: {},
        vidros: {},
        insulfilm: false,
        protecoes: { ...PROTECOES_INICIAIS },
      },
    }
  }
  if (tipo === 'vitima') {
    return {
      vitima: {
        idPerfil: 'adultoMasculino' as const,
        triagem: 'amarelo' as const,
        colarCervical: false,
        local: { tipo: 'solo' as const, postura: 'sentada' as const },
      },
    }
  }
  if (TIPOS_EQUIPAMENTO_NO_CATALOGO.includes(tipo)) {
    return { equipamento: { tipo: tipo as EquipamentoEstabilizacao } }
  }
  if (tipo === 'derramamento') {
    // mancha nova: sem vazamento ativo e aceitando os três materiais — o
    // instrutor ajusta no editor conforme a regra da aula
    return { risco: { vazamentoAtivo: false, materiaisAceitos: [...MATERIAIS_PADRAO_DERRAMAMENTO] } }
  }
  if (tipo === 'caminhaoBombeiros' || tipo === 'resgate') {
    return { cor: '#dc2626' }
  }
  if (tipo === 'onibus') {
    return { cor: COR_PADRAO_ONIBUS }
  }
  return {}
}

/** Montar: bandeja de peças, arraste e gizmo livres. Atender: cena travada, só leitura, dock reduzido a Atendimento/Fila/Cena. */
export type ModoTabletop = 'montar' | 'atender'

/**
 * O cenário preparado, guardado intacto enquanto dura uma sessão de
 * atendimento. A sessão trabalha sobre `EstadoTabletop.cena` (uma cópia);
 * reiniciar volta a esta cena, e voltar a Montar retoma a preparação com o
 * próprio histórico de desfazer.
 */
interface PreparacaoGuardada {
  cena: CenaTabletop
  historico: Historico<CenaTabletop>
}

interface EstadoTabletop {
  /** Em Montar, o próprio cenário; em Atender, a cópia de trabalho da sessão. */
  cena: CenaTabletop
  historico: Historico<CenaTabletop>
  /** Só existe em Atender. */
  preparacao: PreparacaoGuardada | null
  /**
   * Muda a cada sessão iniciada (entrar em Atender, abrir um cenário pronto,
   * reiniciar). Quem guarda estado de atendimento reinicia junto com ele.
   */
  serialSessao: number
  idSelecionado: string | null
  ferramenta: FerramentaTabletop
  modo: ModoTabletop
  /** cena capturada no início de um arrasto de gizmo; enquanto existir, `aplicar` não registra histórico passo a passo */
  cenaAntesDoArrasto: CenaTabletop | null
}

type AcaoReducer =
  | { tipo: 'aplicar'; aplicar: (cena: CenaTabletop) => CenaTabletop }
  | { tipo: 'aplicarNaPreparacao'; aplicar: (cena: CenaTabletop) => CenaTabletop }
  | { tipo: 'reiniciarSessao' }
  | { tipo: 'restaurarSessao'; cenaSessao: CenaTabletop; cenaPreparada: CenaTabletop; mesaCompartilhada?: boolean }
  | { tipo: 'definirCena'; cena: CenaTabletop; modo: ModoTabletop; novaSessao?: boolean }
  | { tipo: 'selecionar'; id: string | null }
  | { tipo: 'ferramenta'; ferramenta: FerramentaTabletop }
  | { tipo: 'modo'; modo: ModoTabletop }
  | { tipo: 'iniciarArrasto' }
  | { tipo: 'finalizarArrasto' }
  | { tipo: 'cancelarArrasto' }
  | { tipo: 'desfazer' }
  | { tipo: 'refazer' }

function reducer(estado: EstadoTabletop, acao: AcaoReducer): EstadoTabletop {
  switch (acao.tipo) {
    case 'aplicar': {
      const nova = acao.aplicar(estado.cena)
      if (nova === estado.cena) return estado
      // durante um arrasto, cada quadro do gizmo chama `aplicar`; o passo de
      // desfazer só é registrado uma vez, em `finalizarArrasto`
      if (estado.cenaAntesDoArrasto) return { ...estado, cena: nova }
      return { ...estado, cena: nova, historico: registrar(estado.historico, estado.cena) }
    }
    case 'aplicarNaPreparacao': {
      // edição que pertence ao cenário, e não à sessão (ex.: escolher a ficha
      // de uma vítima): vale para a cópia em uso e para o cenário guardado
      const comCena = reducer(estado, { tipo: 'aplicar', aplicar: acao.aplicar })
      if (!comCena.preparacao) return comCena
      const cenaPreparada = acao.aplicar(comCena.preparacao.cena)
      if (cenaPreparada === comCena.preparacao.cena) return comCena
      return { ...comCena, preparacao: { ...comCena.preparacao, cena: cenaPreparada } }
    }
    case 'restaurarSessao':
      // sessão salva retomada: volta a Atender com a mesma cópia e o mesmo
      // cenário guardado, SEM mudar o serial — o atendimento restaurado junto
      // não pode ser zerado como numa sessão nova
      return {
        ...estado,
        cena: acao.cenaSessao,
        historico: historicoVazio(),
        preparacao: acao.mesaCompartilhada ? null : { cena: acao.cenaPreparada, historico: historicoVazio() },
        modo: acao.mesaCompartilhada ? 'montar' : 'atender',
        idSelecionado: null,
        cenaAntesDoArrasto: null,
      }
    case 'reiniciarSessao':
      if (!estado.preparacao) return estado
      return {
        ...estado,
        serialSessao: estado.serialSessao + 1,
        cena: estado.preparacao.cena,
        historico: historicoVazio(),
        idSelecionado: null,
        cenaAntesDoArrasto: null,
      }
    case 'definirCena':
      return {
        ...estado,
        cena: acao.cena,
        historico: historicoVazio(),
        // cenário pronto aberto direto em Atender: ele mesmo é a preparação
        preparacao: acao.modo === 'atender' ? { cena: acao.cena, historico: historicoVazio() } : null,
        serialSessao: acao.modo === 'atender' || acao.novaSessao ? estado.serialSessao + 1 : estado.serialSessao,
        idSelecionado: null,
        modo: acao.modo,
        cenaAntesDoArrasto: null,
      }
    case 'selecionar':
      return { ...estado, idSelecionado: acao.id }
    case 'ferramenta':
      return { ...estado, ferramenta: acao.ferramenta }
    case 'modo': {
      // trocar de modo limpa a seleção: o gizmo de Montar e o foco de
      // Atender não fazem sentido apontando pro mesmo objeto por acidente
      const base = { ...estado, modo: acao.modo, idSelecionado: null, cenaAntesDoArrasto: null }
      if (acao.modo === 'atender' && !estado.preparacao) {
        // iniciar o atendimento guarda o cenário e passa a trabalhar numa cópia;
        // desfazer, dentro da sessão, não volta para dentro da montagem
        return {
          ...base,
          preparacao: { cena: estado.cena, historico: estado.historico },
          historico: historicoVazio(),
          serialSessao: estado.serialSessao + 1,
        }
      }
      if (acao.modo === 'montar' && estado.preparacao) {
        // editar a preparação encerra a sessão: a cena volta a ser o cenário
        // guardado, com o mesmo desfazer de antes
        return {
          ...base,
          cena: estado.preparacao.cena,
          historico: estado.preparacao.historico,
          preparacao: null,
        }
      }
      return base
    }
    case 'iniciarArrasto':
      return estado.cenaAntesDoArrasto ? estado : { ...estado, cenaAntesDoArrasto: estado.cena }
    case 'finalizarArrasto': {
      const base = estado.cenaAntesDoArrasto
      if (!base) return estado
      if (base === estado.cena) return { ...estado, cenaAntesDoArrasto: null }
      return { ...estado, cenaAntesDoArrasto: null, historico: registrar(estado.historico, base) }
    }
    case 'cancelarArrasto':
      return estado.cenaAntesDoArrasto
        ? { ...estado, cena: estado.cenaAntesDoArrasto, cenaAntesDoArrasto: null }
        : estado
    case 'desfazer': {
      const resultado = desfazerHistorico(estado.historico, estado.cena)
      if (!resultado) return estado
      return { ...estado, cena: resultado.estado, historico: resultado.historico, idSelecionado: null }
    }
    case 'refazer': {
      const resultado = refazerHistorico(estado.historico, estado.cena)
      if (!resultado) return estado
      return { ...estado, cena: resultado.estado, historico: resultado.historico, idSelecionado: null }
    }
  }
}

function estadoInicial(): EstadoTabletop {
  return {
    cena: carregarTabletopLocal(),
    historico: historicoVazio(),
    preparacao: null,
    serialSessao: 0,
    idSelecionado: null,
    ferramenta: 'translate',
    modo: 'montar',
    cenaAntesDoArrasto: null,
  }
}

export interface ResultadoImportacao {
  sucesso: boolean
  mensagem: string
}

/**
 * Todo o estado do Table top: cena, seleção, ferramenta e histórico de
 * desfazer/refazer, com salvamento automático no navegador.
 *
 * Concentra aqui o que antes eram ~15 `useState`/`useCallback` soltos no
 * `App`, no mesmo espírito de `extracao` (App.tsx) ser um cálculo só: as
 * ações já devolvem a cena pronta, e os componentes só leem o resultado.
 */
export function useTabletop({ mesaCompartilhada = false }: { mesaCompartilhada?: boolean } = {}) {
  const [estado, despachar] = useReducer(reducer, undefined, estadoInicial)
  const cena = estado.cena
  // o que fica salvo é sempre o cenário preparado — nunca a cópia que a
  // sessão de atendimento está alterando
  const cenaParaSalvar = estado.preparacao?.cena ?? cena

  // autosave com debounce — o editor continua utilizável se o navegador
  // bloquear o armazenamento (ex.: modo privado)
  useEffect(() => {
    const agendamento = window.setTimeout(() => {
      try {
        window.localStorage.setItem(CHAVE_TABLETOP_LOCAL, serializarCenaTabletop(cenaParaSalvar))
      } catch {
        // sem persistência; a sessão continua funcionando normalmente
      }
    }, 250)
    return () => window.clearTimeout(agendamento)
  }, [cenaParaSalvar])

  const aplicar = useCallback((fn: (cena: CenaTabletop) => CenaTabletop) => {
    despachar({ tipo: 'aplicar', aplicar: fn })
  }, [])

  const [idOlhandoPara, setIdOlhandoPara] = useState<string | null>(null)
  const [asfaltoAtivo, setAsfaltoAtivo] = useState(true)
  const alternarAsfalto = useCallback(() => setAsfaltoAtivo((prev) => !prev), [])

  const selecionar = useCallback((id: string | null) => despachar({ tipo: 'selecionar', id }), [])

  const escolherFerramenta = useCallback(
    (ferramenta: FerramentaTabletop) => { setIdOlhandoPara(null); despachar({ tipo: 'ferramenta', ferramenta }) },
    [],
  )
  const definirModo = useCallback((modo: ModoTabletop) => despachar({ tipo: 'modo', modo: mesaCompartilhada ? 'montar' : modo }), [mesaCompartilhada])
  /** Retoma uma sessão salva (ver `usePersistenciaSessao`). */
  const restaurarSessao = useCallback(
    (cenaSessao: CenaTabletop, cenaPreparada: CenaTabletop) => despachar({ tipo: 'restaurarSessao', cenaSessao, cenaPreparada, mesaCompartilhada }),
    [mesaCompartilhada],
  )
  /** Volta a cópia da sessão para o cenário preparado, sem apagá-lo nem sair de Atender. */
  const reiniciarSessao = useCallback(() => despachar({ tipo: 'reiniciarSessao' }), [])
  const iniciarManipulacao = useCallback(() => despachar({ tipo: 'iniciarArrasto' }), [])
  const finalizarManipulacao = useCallback(() => despachar({ tipo: 'finalizarArrasto' }), [])
  const aoAlterarManipulacao = useCallback(
    (ativa: boolean) => despachar({ tipo: ativa ? 'iniciarArrasto' : 'finalizarArrasto' }),
    [],
  )
  const cancelarManipulacao = useCallback(() => despachar({ tipo: 'cancelarArrasto' }), [])

  const desfazer = useCallback(() => despachar({ tipo: 'desfazer' }), [])
  const refazer = useCallback(() => despachar({ tipo: 'refazer' }), [])

  const adicionar = useCallback(
    (tipo: TipoObjetoTabletop) => {
      const id = novoIdTabletop()
      const selecionadoAtual = cena.objetos.find((o) => o.id === estado.idSelecionado)

      aplicar((c) => {
        // vítima com um veículo selecionado: já nasce sentada na âncora
        // livre mais próxima, sem precisar de um segundo passo no painel
        if (tipo === 'vitima' && selecionadoAtual?.veiculo) {
          const livre = ancorasLivresDoVeiculo(selecionadoAtual, c.objetos)[0]
          if (livre) {
            const comObjeto = adicionarObjeto(c, { tipo, ...blocoPadraoTabletop(tipo) }, () => id)
            return vincularVitimaAoVeiculo(comObjeto, id, selecionadoAtual.id, livre.id)
          }
        }
        // com qualquer objeto selecionado, o novo nasce ao lado dele — não
        // empilhado exatamente na mesma posição do anterior
        const posicao: TransformacaoTabletop['posicao'] = selecionadoAtual
          ? [selecionadoAtual.transformacao.posicao[0] + 1.6, 0, selecionadoAtual.transformacao.posicao[2]]
          : [0, 0, 0]
        return adicionarObjeto(
          c,
          { tipo, transformacao: { ...TRANSFORMACAO_TABLETOP_PADRAO, posicao }, ...blocoPadraoTabletop(tipo) },
          () => id,
        )
      })
      selecionar(id)
      escolherFerramenta('translate')
    },
    [aplicar, selecionar, escolherFerramenta, cena.objetos, estado.idSelecionado],
  )

  const transformar = useCallback(
    (id: string, transformacao: TransformacaoTabletop) => aplicar((c) => atualizarTransformacao(c, id, transformacao)),
    [aplicar],
  )

  /**
   * Reposicionar uma peça durante a aula é ajuste do cenário, não operação da
   * sessão: vale para a cópia em uso e para o cenário guardado, então não
   * "volta" ao reiniciar a sessão nem ao entrar em Montar.
   */
  const transformarNoCenario = useCallback(
    (id: string, transformacao: TransformacaoTabletop) =>
      despachar({ tipo: 'aplicarNaPreparacao', aplicar: (c) => atualizarTransformacao(c, id, transformacao) }),
    [],
  )

  // --- B2: peça da bandeja aguardando um toque no chão para nascer ali -----
  const [tipoParaPosicionar, setTipoParaPosicionar] = useState<TipoObjetoTabletop | null>(null)

  // trocar pra Atender (ou carregar outra cena) cancela um posicionamento
  // pendente — a bandeja nem aparece mais fora de Montar
  useEffect(() => {
    if (estado.modo === 'atender') setTipoParaPosicionar(null)
  }, [estado.modo])

  const iniciarPosicionamento = useCallback((tipo: TipoObjetoTabletop) => {
    setIdOlhandoPara(null)
    setTipoParaPosicionar(tipo)
  }, [])

  const cancelarPosicionamento = useCallback(() => setTipoParaPosicionar(null), [])

  /** Cria a peça onde foi tocado; encaixe de 0,25 m é opcional; suporta altura Y e rotação em superfícies. */
  const adicionarNaPosicao = useCallback(
    (
      tipo: TipoObjetoTabletop,
      x: number,
      z: number,
      encaixeAtivo = false,
      y = 0,
      rotacao?: readonly [number, number, number],
    ) => {
      const id = novoIdTabletop()
      const [xEncaixado, zEncaixado] = encaixeAtivo ? encaixarPosicaoXZ(x, z) : [x, z]
      const padrao = blocoPadraoTabletop(tipo)
      const bloco = tipo === 'vitima' && y > 0.05 && padrao.vitima
        ? { ...padrao, vitima: { ...padrao.vitima, local: { tipo: 'solo' as const, postura: 'decubitoDorsal' as const } } }
        : padrao
      aplicar((c) =>
        adicionarObjeto(
          c,
          {
            tipo,
            transformacao: {
              ...TRANSFORMACAO_TABLETOP_PADRAO,
              posicao: [xEncaixado, y, zEncaixado],
              rotacao: rotacao ? [...rotacao] : TRANSFORMACAO_TABLETOP_PADRAO.rotacao,
            },
            ...bloco,
          },
          () => id,
        ),
      )
      selecionar(id)
      setTipoParaPosicionar(null)
    },
    [aplicar, selecionar],
  )

  const duplicar = useCallback(
    (id: string) => {
      const novoId = novoIdTabletop()
      aplicar((c) => duplicarObjeto(c, id, () => novoId))
      selecionar(novoId)
    },
    [aplicar, selecionar],
  )

  /** Remover um veículo solta as vítimas vinculadas a ele no chão, na posição de mundo em que estavam. */
  const remover = useCallback(
    (id: string) => {
      aplicar((c) => removerObjeto(soltarVitimasDoVeiculoRemovido(c, id), id))
      selecionar(null)
    },
    [aplicar, selecionar],
  )

  const renomearObjeto = useCallback(
    (id: string, nome: string) => aplicar((c) => atualizarObjeto(c, id, { nome })),
    [aplicar],
  )
  const alterarCor = useCallback(
    (id: string, cor: string) => aplicar((c) => atualizarObjeto(c, id, { cor })),
    [aplicar],
  )
  const resetarTransformacao = useCallback(
    (id: string) => aplicar((c) => atualizarTransformacao(c, id, TRANSFORMACAO_TABLETOP_PADRAO)),
    [aplicar],
  )
  const alternarBloqueio = useCallback(
    (id: string, bloqueado: boolean) => aplicar((c) => atualizarObjeto(c, id, { bloqueado })),
    [aplicar],
  )
  const alternarOculto = useCallback(
    (id: string, oculto: boolean) => aplicar((c) => atualizarObjeto(c, id, { oculto })),
    [aplicar],
  )

  const renomearCenaTabletop = useCallback((nome: string) => aplicar((c) => renomearCena(c, nome)), [aplicar])

  // Table top em branco: começa em Montar, é o ponto de partida de uma cena nova.
  const criarCena = useCallback(() => {
    setIdOlhandoPara(null)
    despachar({ tipo: 'definirCena', cena: criarCenaVazia(), modo: 'montar', novaSessao: mesaCompartilhada })
  }, [mesaCompartilhada])

  // Cenário pronto (preset dos slides ou o exemplo complexo): abre direto em
  // Atender — a montagem já foi feita, o instrutor só precisa conduzir.
  const carregarExemplo = useCallback((idPreset?: string) => {
    setIdOlhandoPara(null)
    const preset = idPreset ? obterPresetPorId(idPreset) : null
    const novaCena = preset ? preset.criarCena() : criarPresetTabletopComplexo()
    despachar({ tipo: 'definirCena', cena: novaCena, modo: mesaCompartilhada ? 'montar' : 'atender', novaSessao: mesaCompartilhada })
  }, [mesaCompartilhada])

  const importar = useCallback((conteudo: string): ResultadoImportacao => {
    try {
      const importada = desserializarCenaTabletop(conteudo)
      setIdOlhandoPara(null)
      // um table top importado normalmente já foi montado em outra sessão
      despachar({ tipo: 'definirCena', cena: importada, modo: mesaCompartilhada ? 'montar' : 'atender', novaSessao: mesaCompartilhada })
      return { sucesso: true, mensagem: 'Table top importado com sucesso.' }
    } catch (erro) {
      return {
        sucesso: false,
        mensagem: erro instanceof Error ? erro.message : 'Arquivo de table top inválido.',
      }
    }
  }, [mesaCompartilhada])

  // --- vítimas: dentro do carro (em qualquer âncora livre) ou fora dele ---

  const vincularVitima = useCallback(
    (idVitima: string, idVeiculo: string, idAncora: string) =>
      aplicar((c) => vincularVitimaAoVeiculo(c, idVitima, idVeiculo, idAncora)),
    [aplicar],
  )
  const desvincularVitima = useCallback(
    (idVitima: string, postura?: PosturaVitimaSolo) => aplicar((c) => desvincularParaOSolo(c, idVitima, postura)),
    [aplicar],
  )
  const moverNaFila = useCallback(
    (idVitima: string, direcao: 'subir' | 'descer') => aplicar((c) => moverNaFilaDominio(c, idVitima, direcao)),
    [aplicar],
  )

  /** Escolha explícita da ficha clínica; `null` deixa a vítima como "ficha não configurada". */
  const vincularFicha = useCallback(
    (idVitima: string, idFicha: string | null) =>
      despachar({ tipo: 'aplicarNaPreparacao', aplicar: (c) => definirFichaDaVitima(c, idVitima, idFicha) }),
    [],
  )

  /** O acionamento do COBOM é preparação: vale para a cópia da sessão e para o cenário guardado. */
  const alterarAcionamento = useCallback(
    (acionamento: AcionamentoCobom) =>
      despachar({ tipo: 'aplicarNaPreparacao', aplicar: (c) => definirAcionamento(c, acionamento) }),
    [],
  )

  const atualizarVitima = useCallback(
    (id: string, alteracoes: Partial<BlocoVitimaTabletop>) => aplicar((c) => atualizarObjeto(c, id, { vitima: alteracoes })),
    [aplicar],
  )

  const atualizarRisco = useCallback(
    (id: string, alteracoes: Partial<BlocoRiscoTabletop>) => aplicar((c) => atualizarObjeto(c, id, { risco: alteracoes })),
    [aplicar],
  )

  const atualizarVeiculo = useCallback(
    (id: string, alteracoes: Partial<BlocoVeiculoTabletop>) => aplicar((c) => atualizarObjeto(c, id, { veiculo: alteracoes })),
    [aplicar],
  )

  /**
   * Trocar a posição final não é só gravar o campo: a rotação e a altura de
   * apoio do objeto mudam junto (mantendo a guinada escolhida), do mesmo
   * jeito que o botão de posição final descreve no painel.
   */
  const definirPosicaoFinal = useCallback(
    (id: string, posicaoFinal: PosicaoFinalVeiculo) =>
      aplicar((c) => {
        const objeto = c.objetos.find((o) => o.id === id)
        if (objeto?.tipo === 'onibus') {
          if (!(POSICOES_FINAIS_ONIBUS as readonly string[]).includes(posicaoFinal)) return c
          const pose = poseDoOnibus(posicaoFinal as PosicaoFinalOnibus, objeto.transformacao.rotacao[1])
          return atualizarObjeto(c, id, {
            transformacao: { rotacao: pose.rotacao, posicao: [objeto.transformacao.posicao[0], pose.altura, objeto.transformacao.posicao[2]] },
          })
        }
        if (!objeto?.veiculo) return c
        const perfil = perfilVeiculoPorId(objeto.veiculo.idPerfil)
        const guinadaAtual = objeto.transformacao.rotacao[1]
        const pose = poseDaPosicaoFinal(perfil, posicaoFinal, guinadaAtual)
        const comBloco = atualizarObjeto(c, id, { veiculo: { posicaoFinal } })
        return atualizarObjeto(comBloco, id, {
          transformacao: { rotacao: pose.rotacao, posicao: [objeto.transformacao.posicao[0], pose.altura, objeto.transformacao.posicao[2]] },
        })
      }),
    [aplicar],
  )

  // guarda a mensagem didática da última ação de vidro, para o painel exibir
  // sem precisar recalcular a mesma ação por conta própria
  const [ultimoResultadoVidro, setUltimoResultadoVidro] = useState<ResultadoAcaoVidro | null>(null)

  const executarManejoVidro = useCallback(
    (idVeiculo: string, idVidro: IdVidro, ferramenta: FerramentaVidro) => {
      const resultado = executarManejoVidroNoVeiculo(cena, idVeiculo, idVidro, ferramenta)
      setUltimoResultadoVidro(resultado?.resultado ?? null)
      if (resultado?.resultado?.sucesso) {
        tocarSomEstilhacoVidro()
      }
      if (resultado) aplicar(() => resultado.cena)
    },
    [aplicar, cena],
  )

  const restaurarVidros = useCallback(
    (idVeiculo: string) => {
      setUltimoResultadoVidro(null)
      aplicar((c) => restaurarVidrosDoVeiculo(c, idVeiculo))
    },
    [aplicar],
  )

  const [ultimoResultadoCorte, setUltimoResultadoCorte] = useState<ResultadoIntervencaoVeiculoTabletop | null>(null)

  const aplicarDeformacaoVeiculo = useCallback(
    (idVeiculo: string, nivel: NivelDanoVeicular | 'nenhum', intrusaoM?: number,
      regiao: RegiaoDanoVeicular = 'frente', idPorta?: IdPortaTabletop) => {
      aplicar((c) => {
        const perfilId = c.objetos.find((o) => o.id === idVeiculo)?.veiculo?.idPerfil
        if (!perfilId) return c
        const deformacao = nivel === 'nenhum'
          ? null
          : idPorta
            ? deformacaoPredefinidaNaPorta(perfilVeiculoPorId(perfilId), idPorta, nivel, intrusaoM)
            : {
                ...calcularDeformacaoFrontalPredefinida(nivel),
                regiao,
                ...(intrusaoM !== undefined ? { intrusaoM } : {}),
              }
        return aplicarDeformacaoNaRegiao(c, idVeiculo, regiao, deformacao, idPorta)
      })
    },
    [aplicar],
  )

  const aplicarAmassadoNoPonto = useCallback((idVeiculo: string, ponto: Readonly<{ x: number; y: number; z: number }>, larguraM: number, intrusaoM: number) => {
    aplicar((c) => adicionarAmassadoNoPonto(c, idVeiculo, ponto, larguraM, intrusaoM))
  }, [aplicar])

  const limparAmassados = useCallback((idVeiculo: string) => {
    aplicar((c) => limparAmassadosDoVeiculo(c, idVeiculo))
  }, [aplicar])

  const executarCorteVeiculo = useCallback(
    (idVeiculo: string, idPeca: string, novoEstado: EstadoCortePecaTabletop) => {
      const res = executarCorteNoVeiculo(cena, idVeiculo, idPeca, novoEstado)
      setUltimoResultadoCorte(res.resultado)
      if (res.resultado.sucesso) {
        tocarSomCorteMetal()
      }
      aplicar(() => res.cena)
    },
    [aplicar, cena],
  )

  const selecionarPecaVeiculo = useCallback(
    (idVeiculo: string, idPeca: string | null) => {
      aplicar((c) => selecionarPecaNoVeiculo(c, idVeiculo, idPeca))
    },
    [aplicar],
  )

  const restaurarEstruturaVeiculo = useCallback(
    (idVeiculo: string) => {
      setUltimoResultadoCorte(null)
      aplicar((c) => restaurarEstruturaDoVeiculo(c, idVeiculo))
    },
    [aplicar],
  )

  // "Testar estabilidade" é uma ação explícita — selecionar o veículo não o balança
  const [pedidoTesteBalanco, setPedidoTesteBalanco] = useState<PedidoTesteBalanco | null>(null)
  const testarBalanco = useCallback((idVeiculo: string) => {
    setPedidoTesteBalanco((anterior) => ({ idVeiculo, serial: (anterior?.serial ?? 0) + 1 }))
  }, [])

  // Cenário travado: veículos e obstáculos fixos, apenas ferramentas/equipamentos manipuláveis
  const [cenarioTravado, setCenarioTravado] = useState(false)
  const alternarTravaCenario = useCallback(() => setCenarioTravado((anterior) => !anterior), [])

  const ancorasLivres = useCallback(
    (idVeiculo: string) => {
      const veiculo = cena.objetos.find((o) => o.id === idVeiculo)
      return veiculo ? ancorasLivresDoVeiculo(veiculo, cena.objetos) : []
    },
    [cena.objetos],
  )

  const objetoSelecionado: ObjetoTabletop | null = useMemo(
    () => cena.objetos.find((o) => o.id === estado.idSelecionado) ?? null,
    [cena.objetos, estado.idSelecionado],
  )

  const fila = useMemo(() => filaDeAtendimento(cena.objetos), [cena.objetos])

  const cancelarOlharPara = useCallback(() => setIdOlhandoPara(null), [])
  useEffect(() => {
    if (idOlhandoPara && !cena.objetos.some(o => o.id === idOlhandoPara)) cancelarOlharPara()
  }, [cena.objetos, idOlhandoPara, cancelarOlharPara])

  const podeVirar = (o: ObjetoTabletop) => !o.bloqueado && (!cenarioTravado || Boolean(o.equipamento))
  const definirDirecao = (id: string, graus: number) => aplicar(c => {
    const objeto = c.objetos.find(o => o.id === id)
    if (!objeto || !podeVirar(objeto) || objeto.vitima?.local.tipo === 'veiculo') return c
    const t = comDirecao(objeto.transformacao, graus)
    if (t.rotacao[1] === objeto.transformacao.rotacao[1]) return c
    return atualizarTransformacao(c, id, t)
  })
  const girar = (id: string, delta: number) => {
    const objeto = cena.objetos.find(o => o.id === id)
    if (objeto) definirDirecao(id, objeto.transformacao.rotacao[1] * 180 / Math.PI + delta)
  }
  const iniciarOlharPara = (id: string) => {
    const objeto = cena.objetos.find(o => o.id === id)
    if (!objeto || !podeVirar(objeto) || !['bombeiro', 'bombeiroMedico'].includes(objeto.tipo)) return
    cancelarPosicionamento()
    selecionar(id)
    escolherFerramenta('translate')
    setIdOlhandoPara(id)
  }
  const selecionarNaMesa = (id: string | null) => {
    if (!idOlhandoPara) { selecionar(id); return }
    const origem = cena.objetos.find(o => o.id === idOlhandoPara)
    const alvo = cena.objetos.find(o => o.id === id)
    if (!origem || !alvo || origem.id === alvo.id || (!alvo.vitima && !['carro', 'suv', 'caminhao', 'onibus', 'caminhaoBombeiros', 'resgate', 'moto'].includes(alvo.tipo))) return
    const direcao = direcaoParaAlvo(cena, origem, alvo)
    if (direcao === null) return
    definirDirecao(origem.id, direcao)
    cancelarOlharPara()
  }
  const alterarHistoria = (historiaInicial: string) => aplicar(c =>
    c.historiaInicial === historiaInicial ? c : { ...c, historiaInicial })

  return {
    cena,
    idSelecionado: estado.idSelecionado,
    objetoSelecionado,
    ferramenta: estado.ferramenta,
    modo: estado.modo,
    mesaCompartilhada,
    alterarHistoria,
    girar,
    definirDirecao,
    idOlhandoPara,
    iniciarOlharPara,
    cancelarOlharPara,
    selecionarNaMesa,
    /** Há um cenário preparado guardado (modo Atender): `reiniciarSessao` tem para onde voltar. */
    emSessao: estado.preparacao !== null,
    serialSessao: estado.serialSessao,
    /** O cenário guardado durante uma sessão (Atender); `null` em Montar. */
    cenaPreparada: estado.preparacao?.cena ?? null,
    restaurarSessao,
    fila,
    podeDesfazer: podeDesfazerHistorico(estado.historico),
    podeRefazer: podeRefazerHistorico(estado.historico),
    selecionar,
    escolherFerramenta,
    definirModo,
    reiniciarSessao,
    iniciarManipulacao,
    finalizarManipulacao,
    aoAlterarManipulacao,
    cancelarManipulacao,
    desfazer,
    refazer,
    adicionar,
    transformar,
    transformarNoCenario,
    tipoParaPosicionar,
    iniciarPosicionamento,
    cancelarPosicionamento,
    adicionarNaPosicao,
    duplicar,
    remover,
    renomearObjeto,
    alterarCor,
    resetarTransformacao,
    alternarBloqueio,
    alternarOculto,
    renomearCena: renomearCenaTabletop,
    criarCena,
    carregarExemplo,
    importar,
    vincularVitima,
    desvincularVitima,
    moverNaFila,
    ancorasLivres,
    atualizarVitima,
    vincularFicha,
    alterarAcionamento,
    atualizarRisco,
    atualizarVeiculo,
    definirPosicaoFinal,
    executarManejoVidro,
    restaurarVidros,
    ultimoResultadoVidro,
    aplicarDeformacaoVeiculo,
    aplicarAmassadoNoPonto,
    limparAmassados,
    executarCorteVeiculo,
    selecionarPecaVeiculo,
    restaurarEstruturaVeiculo,
    ultimoResultadoCorte,
    pedidoTesteBalanco,
    testarBalanco,
    asfaltoAtivo,
    alternarAsfalto,
    setAsfaltoAtivo,
    cenarioTravado,
    alternarTravaCenario,
    setCenarioTravado,
  }
}



